import { useState, useEffect } from "react"
import { useData } from "../contexts/DataContext"
import Navigation from "../components/Navigation"
import Header from "../components/Header"
import NotificationToaster from "../components/NotificationToaster"
import Login from "../components/Login"
import LogoutConfirmModal from "../components/LogoutConfirmModal"
import useLogout from "../hooks/useLogout"
import {
  globalPropertiesAPI,
  propertyGroupsAPI,
  propertySetsAPI,
  reportsAPI
} from "../services/componentServices"
import '../styles.css';
import '../button-styles.css';

const PropertiesPage = ({
  currentUser,
  onLogout,
  onShowUserManagement,
  onTabChange,
  activeTab,
  sidebarOpen,
  onToggleSidebar,
}) => {
  const { savedProperties, deleteProperties, saveProperties } = useData()
  const [notifications, setNotifications] = useState([])
  const [activeSubTab, setActiveSubTab] = useState("global")
  const [showAddPropertyPopup, setShowAddPropertyPopup] = useState(false)
  const [newProperty, setNewProperty] = useState({
    property: "",
    uom: "",
    basis: "volume",
    linear: true,
    min: "",
    max: "",
    description: "",
    index_formula: "",
    reverse_formula: "",
  })
  const [editingIndex, setEditingIndex] = useState(null)

  const [selectedProperties, setSelectedProperties] = useState([])
  const [groupName, setGroupName] = useState("")
  const [savedGroups, setSavedGroups] = useState([])
  const [expandedGroups, setExpandedGroups] = useState({})

  const [savedPropertySets, setSavedPropertySets] = useState([])
  const [propertySetName, setPropertySetName] = useState("")
  const [propertySetMin, setPropertySetMin] = useState("")
  const [propertySetMax, setPropertySetMax] = useState("")
  const [expandedPropertySets, setExpandedPropertySets] = useState({})

  const [globalProperties, setGlobalProperties] = useState([])

  const [allUniqueProperties, setAllUniqueProperties] = useState([])
  const [savedPropertiesFromApp, setSavedPropertiesFromApp] = useState([])
  const [isSavingToDB, setIsSavingToDB] = useState(false)
  const [hasAutoSaved, setHasAutoSaved] = useState(false)

  const [showGradePropertyPopup, setShowGradePropertyPopup] = useState(false)
  const [savedPropertiesPage, setSavedPropertiesPage] = useState(1)
  const [globalPropertiesPage, setGlobalPropertiesPage] = useState(1)
  const [savedGroupsPage, setSavedGroupsPage] = useState(1)
  const [savedPropertySetsPage, setSavedPropertySetsPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  const [editingGroup, setEditingGroup] = useState(null)
  const [editingPropertySet, setEditingPropertySet] = useState(null)
  const [showEditGroupPopup, setShowEditGroupPopup] = useState(false)
  const [showEditPropertySetPopup, setShowEditPropertySetPopup] = useState(false)
  const [groupProperties, setGroupProperties] = useState([])
  const [propertySetProperties, setPropertySetProperties] = useState([])

  const [formErrors, setFormErrors] = useState({})

  // Loading states
  const [isLoading, setIsLoading] = useState({
    global: true,
    groups: true,
    propertySets: true,
    allProperties: true
  })

  const [editingPropertySetProperties, setEditingPropertySetProperties] = useState([])
  const [editingPropertySetMin, setEditingPropertySetMin] = useState("")
  const [editingPropertySetMax, setEditingPropertySetMax] = useState("")

  // Use the common logout hook
  const {
    showLogoutConfirm,
    shouldRefresh,
    handleLogoutClick,
    handleConfirmLogout,
    handleCancelLogout,
    setShouldRefresh
  } = useLogout()

  const basisOptions = [
    { value: "volume", label: "Volume" },
    { value: "mass", label: "Mass/Weight" },
  ]

  const blendingTypeOptions = [
    { value: true, label: "Linear" },
    { value: false, label: "Non-Linear" },
  ]

  useEffect(() => {
    if (!currentUser && shouldRefresh) {
      window.location.reload();
    }
  }, [currentUser, shouldRefresh]);

  // API Functions using the service
  const fetchGlobalProperties = async () => {
    try {
      setIsLoading(prev => ({ ...prev, global: true }));
      const data = await globalPropertiesAPI.fetchAll();
      console.log('Fetched global properties:', data);
      setGlobalProperties(data.data || []);
    } catch (error) {
      console.error('Failed to fetch global properties:', error);
      addNotification('error', 'Failed to load global properties');
    } finally {
      setIsLoading(prev => ({ ...prev, global: false }));
    }
  };

  const createGlobalProperty = async (propertyData) => {
    try {
      const data = await globalPropertiesAPI.create(propertyData);
      return data;
    } catch (error) {
      console.error('Failed to create global property:', error);
      throw error;
    }
  };

  const updateGlobalProperty = async (propertyId, propertyData) => {
    try {
      const data = await globalPropertiesAPI.update(propertyId, propertyData);
      return data;
    } catch (error) {
      console.error('Failed to update global property:', error);
      throw error;
    }
  };

  const deleteGlobalProperty = async (propertyId) => {
    try {
      await globalPropertiesAPI.delete(propertyId);
    } catch (error) {
      console.error('Failed to delete global property:', error);
      throw error;
    }
  };

  // Property Groups API
  const fetchPropertyGroups = async () => {
    try {
      setIsLoading(prev => ({ ...prev, groups: true }));
      const data = await propertyGroupsAPI.fetchAll();
      console.log('Fetched groups:', data);
      setSavedGroups(data.data || []);
    } catch (error) {
      console.error('Failed to fetch property groups:', error);
      addNotification('error', 'Failed to load property groups');
    } finally {
      setIsLoading(prev => ({ ...prev, groups: false }));
    }
  };

  const createPropertyGroup = async (groupData) => {
    try {
      const data = await propertyGroupsAPI.create(groupData);
      return data;
    } catch (error) {
      console.error('Failed to create property group:', error);
      throw error;
    }
  };

  const updatePropertyGroup = async (groupId, groupData) => {
    try {
      const data = await propertyGroupsAPI.update(groupId, groupData);
      return data;
    } catch (error) {
      console.error('Failed to update property group:', error);
      throw error;
    }
  };

  const deletePropertyGroup = async (groupId) => {
    try {
      await propertyGroupsAPI.delete(groupId);
    } catch (error) {
      console.error('Failed to delete property group:', error);
      throw error;
    }
  };

  // Property Sets API
  const fetchPropertySets = async () => {
    try {
      setIsLoading(prev => ({ ...prev, propertySets: true }));
      const data = await propertySetsAPI.fetchAll();
      console.log('Fetched property sets:', data);
      setSavedPropertySets(data.data || []);
    } catch (error) {
      console.error('Failed to fetch property sets:', error);
      addNotification('error', 'Failed to load property sets');
    } finally {
      setIsLoading(prev => ({ ...prev, propertySets: false }));
    }
  };

  const createPropertySet = async (setData) => {
    try {
      const data = await propertySetsAPI.create(setData);
      return data;
    } catch (error) {
      console.error('Failed to create property set:', error);
      throw error;
    }
  };

  const updatePropertySet = async (setId, setData) => {
    try {
      const data = await propertySetsAPI.update(setId, setData);
      return data;
    } catch (error) {
      console.error('Failed to update property set:', error);
      throw error;
    }
  };

  const deletePropertySet = async (setId) => {
    try {
      await propertySetsAPI.delete(setId);
    } catch (error) {
      console.error('Failed to delete property set:', error);
      throw error;
    }
  };

  const fetchAllPropertiesReport = async () => {
    try {
      setIsLoading(prev => ({ ...prev, allProperties: true }));
      const data = await reportsAPI.fetchAllProperties();
      return data.data || [];
    } catch (error) {
      console.error('Failed to fetch all properties report:', error);
      addNotification('error', 'Failed to load all properties report');
      return [];
    } finally {
      setIsLoading(prev => ({ ...prev, allProperties: false }));
    }
  };


  const getCombinedProperties = () => {
    const combined = [...globalProperties];

    savedPropertiesFromApp.forEach(property => {
      const exists = combined.some(p =>
        p.property === property.property &&
        p.uom === property.uom
      );

      if (!exists) {
        combined.push({
          ...property,
          isFromApp: true,
          sourceType: 'app'
        });
      }
    });

    allUniqueProperties.forEach(property => {
      const exists = combined.some(p =>
        p.property === property.property &&
        p.uom === property.uom
      );

      if (!exists) {
        combined.push({
          ...property,
          isFromReport: true,
          sourceType: 'report'
        });
      }
    });

    return combined;
  };

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        fetchGlobalProperties(),
        fetchPropertyGroups(),
        fetchPropertySets()
      ]);
    };

    loadData();
  }, [])

  useEffect(() => {
    const loadAndMergeAllProperties = async () => {
      const properties = await fetchAllPropertiesReport();
      setAllUniqueProperties(properties);
    };
    loadAndMergeAllProperties();
  }, []);

  const addNotification = (type, message) => {
    const id = Date.now()
    setNotifications((prev) => [...prev, { id, type, message }])

    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id))
    }, 3000)
  }

  const validatePropertyForm = () => {
    const errors = {}

    if (!newProperty.property.trim()) {
      errors.property = "Property name is required"
    }

    if (!newProperty.uom.trim()) {
      errors.uom = "UOM name is required"
    }

    if (!newProperty.linear) {
      if (!newProperty.index_formula.trim()) {
        errors.index_formula = "Index formula is required for non-linear properties"
      }

      if (!newProperty.reverse_formula.trim()) {
        errors.reverse_formula = "Reverse formula is required for non-linear properties"
      }

      if (newProperty.index_formula.trim() && !newProperty.index_formula.includes('x')) {
        errors.index_formula = "Index formula must contain 'x' as variable"
      }

      if (newProperty.reverse_formula.trim() && !newProperty.reverse_formula.includes('x')) {
        errors.reverse_formula = "Reverse formula must contain 'x' as variable"
      }
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const getPaginatedData = (data, currentPage, pageSize = itemsPerPage) => {
    const startIndex = (currentPage - 1) * pageSize
    const endIndex = startIndex + pageSize
    return data.slice(startIndex, endIndex)
  }

  const getTotalPages = (data, pageSize = itemsPerPage) => {
    return Math.ceil(data.length / pageSize)
  }

  const handlePageChange = (page, setPageFunction) => {
    setPageFunction(page)
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  const renderPagination = (currentPage, totalPages, setPageFunction, dataLength) => {
    if (totalPages <= 1) return null

    const pageNumbers = []
    const maxVisiblePages = 5

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1)
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i)
    }

    return (
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div className="text-sm text-gray-700 text-center sm:text-left">
          Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{" "}
          <span className="font-medium">{Math.min(currentPage * itemsPerPage, dataLength)}</span> of{" "}
          <span className="font-medium">{dataLength}</span> results
        </div>

        <div className="flex flex-col xs:flex-row items-center gap-2 w-full sm:w-auto">
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value))
              setPageFunction(1)
            }}
            className="px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full xs:w-auto"
          >
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
          </select>

          <div className="flex items-center gap-1">
            <button
              onClick={() => handlePageChange(currentPage - 1, setPageFunction)}
              disabled={currentPage === 1}
              className={`px-3 py-1.5 rounded border text-sm font-medium min-w-[70px] ${currentPage === 1
                ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-300"
                : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
                }`}
            >
              Previous
            </button>

            <div className="flex gap-1 overflow-x-auto max-w-[200px] sm:max-w-none">
              {startPage > 1 && (
                <>
                  <button
                    onClick={() => handlePageChange(1, setPageFunction)}
                    className="px-3 py-1.5 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-sm font-medium min-w-[40px]"
                  >
                    1
                  </button>
                  {startPage > 2 && <span className="px-2 py-1.5 text-gray-500">...</span>}
                </>
              )}

              {pageNumbers.map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page, setPageFunction)}
                  className={`px-3 py-1.5 rounded border text-sm font-medium min-w-[40px] ${currentPage === page
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
                    }`}
                >
                  {page}
                </button>
              ))}

              {endPage < totalPages && (
                <>
                  {endPage < totalPages - 1 && <span className="px-2 py-1.5 text-gray-500">...</span>}
                  <button
                    onClick={() => handlePageChange(totalPages, setPageFunction)}
                    className="px-3 py-1.5 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-sm font-medium min-w-[40px]"
                  >
                    {totalPages}
                  </button>
                </>
              )}
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1, setPageFunction)}
              disabled={currentPage === totalPages}
              className={`px-3 py-1.5 rounded border text-sm font-medium min-w-[70px] ${currentPage === totalPages
                ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-300"
                : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
                }`}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Loading Spinner Component
  const LoadingSpinner = ({ size = "medium", text = "Loading..." }) => {
    const sizeClasses = {
      small: "w-6 h-6",
      medium: "w-8 h-8",
      large: "w-12 h-12"
    };

    return (
      <div className="flex flex-col items-center justify-center p-8">
        <div className={`animate-spin rounded-full border-4 border-gray-300 border-t-blue-600 ${sizeClasses[size]}`}></div>
        {text && <p className="mt-4 text-gray-600 text-sm">{text}</p>}
      </div>
    );
  };

  const handleUpdateGroupName = async (groupId, newName) => {
    try {
      const group = savedGroups.find(g => g._id === groupId);
      if (!group) return;

      const updatedGroup = {
        ...group,
        name: newName
      };

      await updatePropertyGroup(groupId, updatedGroup);
      await fetchPropertyGroups();

      const existingGroups = JSON.parse(localStorage.getItem('propertyGroups') || '[]');
      const updatedGroups = existingGroups.map(g => g._id === groupId || g.id === groupId ? updatedGroup : g);
      localStorage.setItem('propertyGroups', JSON.stringify(updatedGroups));

      window.dispatchEvent(new CustomEvent('propertyGroupsUpdated', {
        detail: { groups: updatedGroups }
      }));

      addNotification("success", "Group name updated successfully");
    } catch (error) {
      console.error('Error updating group name:', error);
      addNotification("error", "Failed to update group name");
    }
  };

  const handleUpdatePropertySetName = async (setId, newName) => {
    try {
      const propertySet = savedPropertySets.find(s => s._id === setId);
      if (!propertySet) return;

      const updatedSet = {
        ...propertySet,
        name: newName
      };

      await updatePropertySet(setId, updatedSet);
      await fetchPropertySets();

      const existingSets = JSON.parse(localStorage.getItem('savedPropertySets') || '[]');
      const updatedSets = existingSets.map(s => s._id === setId || s.id === setId ? updatedSet : s);
      localStorage.setItem('savedPropertySets', JSON.stringify(updatedSets));

      window.dispatchEvent(new CustomEvent('propertySetsUpdated', {
        detail: { sets: updatedSets }
      }));

      addNotification("success", "Property set name updated successfully");
    } catch (error) {
      console.error('Error updating property set name:', error);
      addNotification("error", "Failed to update property set name");
    }
  };

  const handleEditGroup = (group) => {
    setEditingGroup(group)
    setGroupProperties([...group.properties])
    setShowEditGroupPopup(true)
  }

  const handleSaveGroupEdit = async () => {
    if (!editingGroup) return

    try {
      const updatedGroup = {
        ...editingGroup,
        properties: groupProperties
      }

      await updatePropertyGroup(editingGroup._id, updatedGroup)
      await fetchPropertyGroups()

      const existingGroups = JSON.parse(localStorage.getItem('propertyGroups') || '[]');
      const updatedGroups = existingGroups.map(g => g._id === editingGroup._id || g.id === editingGroup._id ? updatedGroup : g);
      localStorage.setItem('propertyGroups', JSON.stringify(updatedGroups));

      window.dispatchEvent(new CustomEvent('propertyGroupsUpdated', {
        detail: { groups: updatedGroups }
      }));

      setShowEditGroupPopup(false)
      setEditingGroup(null)
      setGroupProperties([])

      addNotification("success", "Group updated successfully")
    } catch (error) {
      console.error('Error updating group:', error)
      addNotification("error", "Failed to update group")
    }
  }

  const handleAddPropertyToGroup = (property) => {
    const exists = groupProperties.some(p =>
      p.property === property.property && p.uom === property.uom
    )

    if (!exists) {
      setGroupProperties(prev => [...prev, {
        ...property,
        source: 'global'
      }])
      addNotification("success", "Property added to group")
    } else {
      addNotification("info", "Property already exists in group")
    }
  }

  const handleRemovePropertyFromGroup = (index) => {
    setGroupProperties(prev => prev.filter((_, i) => i !== index))
    addNotification("info", "Property removed from group")
  }

  const handleEditPropertySet = (propertySet) => {
    setEditingPropertySet(propertySet)
    setEditingPropertySetProperties([...propertySet.properties.map(prop => ({ ...prop }))])
    setEditingPropertySetMin(propertySet.setMin || "")
    setEditingPropertySetMax(propertySet.setMax || "")
    setShowEditPropertySetPopup(true)
  }

  const handleSavePropertySetEdit = async () => {
    if (!editingPropertySet) return

    try {
      const updatedPropertySet = {
        ...editingPropertySet,
        properties: editingPropertySetProperties,
        setMin: editingPropertySetMin,
        setMax: editingPropertySetMax
      }

      await updatePropertySet(editingPropertySet._id, updatedPropertySet)
      await fetchPropertySets()

      const existingSets = JSON.parse(localStorage.getItem('savedPropertySets') || '[]');
      const updatedSets = existingSets.map(s => s._id === editingPropertySet._id || s.id === editingPropertySet._id ? updatedPropertySet : s);
      localStorage.setItem('savedPropertySets', JSON.stringify(updatedSets));

      window.dispatchEvent(new CustomEvent('propertySetsUpdated', {
        detail: { sets: updatedSets }
      }));

      setShowEditPropertySetPopup(false)
      setEditingPropertySet(null)
      setEditingPropertySetProperties([])
      setEditingPropertySetMin("")
      setEditingPropertySetMax("")

      addNotification("success", "Property set updated successfully")
    } catch (error) {
      console.error('Error updating property set:', error)
      addNotification("error", "Failed to update property set")
    }
  }

  const handlePropertyMinMaxChange = (index, field, value) => {
    const updatedProperties = [...editingPropertySetProperties]
    updatedProperties[index] = {
      ...updatedProperties[index],
      [field]: value
    }
    setEditingPropertySetProperties(updatedProperties)
  }

  const handleAddPropertyToPropertySet = (property) => {
    const exists = editingPropertySetProperties.some(p =>
      p.property === property.property && p.uom === property.uom
    )

    if (!exists) {
      setEditingPropertySetProperties(prev => [...prev, {
        ...property,
        min: "",
        max: "",
        source: 'global'
      }])
      addNotification("success", "Property added to property set")
    } else {
      addNotification("info", "Property already exists in property set")
    }
  }

  const handleRemovePropertyFromPropertySet = (index) => {
    setEditingPropertySetProperties(prev => prev.filter((_, i) => i !== index))
    addNotification("info", "Property removed from property set")
  }

  const handleSaveAsGroup = async () => {
    if (selectedProperties.length === 0) {
      addNotification("error", "Please select at least one property")
      return
    }

    if (!groupName.trim()) {
      addNotification("error", "Please enter a group name")
      return
    }

    try {
      const groupData = {
        name: groupName.trim(),
        properties: selectedProperties.map((p) => ({
          property: p.property,
          uom: p.uom,
          basis: p.basis,
          linear: p.linear,
          min: p.min,
          max: p.max,
          description: p.description,
          index_formula: p.index_formula,
          reverse_formula: p.reverse_formula,
          source: p.source,
          sourceId: p.sourceId,
        })),
      }

      console.log('Creating property group:', groupData);
      const result = await createPropertyGroup(groupData)

      await fetchPropertyGroups()

      const existingGroups = JSON.parse(localStorage.getItem('propertyGroups') || '[]');
      const updatedGroups = [...existingGroups, {
        ...groupData,
        _id: result.data?._id || `local-${Date.now()}`,
        id: result.data?._id || `local-${Date.now()}`,
        createdAt: new Date().toISOString()
      }];
      localStorage.setItem('propertyGroups', JSON.stringify(updatedGroups));

      window.dispatchEvent(new CustomEvent('propertyGroupsUpdated', {
        detail: { groups: updatedGroups }
      }));

      setGroupName("")
      setSelectedProperties([])
      setShowGradePropertyPopup(false)

      addNotification(
        "success",
        `Group "${groupData.name}" saved successfully with ${groupData.properties.length} properties`,
      )

    } catch (error) {
      addNotification("error", "Failed to save property group")
    }
  }

  const handleSaveAsPropertySet = async () => {
    if (selectedProperties.length === 0) {
      addNotification("error", "Please select at least one property")
      return
    }

    if (!propertySetName.trim()) {
      addNotification("error", "Please enter a property set name")
      return
    }

    try {
      const propertySetData = {
        name: propertySetName.trim(),
        properties: selectedProperties.map((p) => ({
          property: p.property,
          uom: p.uom,
          basis: p.basis,
          linear: p.linear,
          // min: propertySetMin || p.min, 
          // max: propertySetMax || p.max,
          min: "",
          max: "",
          description: p.description,
          index_formula: p.index_formula,
          reverse_formula: p.reverse_formula,
          source: p.source,
          sourceId: p.sourceId,
        })),
        setMin: propertySetMin,
        setMax: propertySetMax,
      }

      console.log('Creating property set:', propertySetData);
      const result = await createPropertySet(propertySetData)
      await fetchPropertySets()

      const existingSets = JSON.parse(localStorage.getItem('savedPropertySets') || '[]');
      const updatedSets = [...existingSets, {
        ...propertySetData,
        _id: result.data?._id || `local-${Date.now()}`,
        id: result.data?._id || `local-${Date.now()}`,
        createdAt: new Date().toISOString()
      }];
      localStorage.setItem('savedPropertySets', JSON.stringify(updatedSets));

      window.dispatchEvent(new CustomEvent('propertySetsUpdated', {
        detail: { sets: updatedSets }
      }));

      setPropertySetName("")
      setPropertySetMin("")
      setPropertySetMax("")
      setSelectedProperties([])
      setShowGradePropertyPopup(false)

      addNotification(
        "success",
        `Property Set "${propertySetData.name}" saved successfully with ${propertySetData.properties.length} properties`,
      )
    } catch (error) {
      console.error('Error creating property set:', error);
      addNotification("error", "Failed to save property set")
    }
  }

  const handleDeletePropertySet = async (setId) => {
    if (window.confirm("Are you sure you want to delete this property set?")) {
      try {
        await deletePropertySet(setId)
        await fetchPropertySets()

        const existingSets = JSON.parse(localStorage.getItem('savedPropertySets') || '[]');
        const updatedSets = existingSets.filter(set => set._id !== setId && set.id !== setId);
        localStorage.setItem('savedPropertySets', JSON.stringify(updatedSets));

        window.dispatchEvent(new CustomEvent('propertySetsUpdated', {
          detail: { sets: updatedSets }
        }));

        addNotification("success", "Property set deleted successfully")
      } catch (error) {
        addNotification("error", "Failed to delete property set")
      }
    }
  }

  const handleClearAllPropertySets = async () => {
    if (window.confirm("Are you sure you want to delete all property sets? This action cannot be undone.")) {
      try {
        for (const set of savedPropertySets) {
          await deletePropertySet(set._id || set.id)
        }
        await fetchPropertySets()

        localStorage.setItem('savedPropertySets', JSON.stringify([]));

        window.dispatchEvent(new CustomEvent('propertySetsUpdated', {
          detail: { sets: [] }
        }));

        addNotification("info", "All property sets cleared")
      } catch (error) {
        addNotification("error", "Failed to clear property sets")
      }
    }
  }

  const togglePropertySet = (setId) => {
    setExpandedPropertySets((prev) => ({
      ...prev,
      [setId]: !prev[setId],
    }))
  }

  const handleAddPropertyClick = () => {
    setShowAddPropertyPopup(true)
    setEditingIndex(null)
    setNewProperty({
      property: "",
      uom: "",
      basis: "volume",
      linear: true,
      min: "",
      max: "",
      description: "",
      index_formula: "",
      reverse_formula: "",
    })
    setFormErrors({})
  }

  const handleSaveProperty = async () => {
    if (!validatePropertyForm()) {
      addNotification("error", "Please fix the form errors before saving")
      return
    }

    const propertyToSave = {
      ...newProperty,
      property: newProperty.property.trim(),
      uom: newProperty.uom.trim(),
      basis: newProperty.basis,
      linear: newProperty.linear,
      // min: newProperty.min,
      // max: newProperty.max,
      min: newProperty.min || "", // Empty string if not provided
      max: newProperty.max || "",
      description: newProperty.description,
      index_formula: newProperty.index_formula,
      reverse_formula: newProperty.reverse_formula,
    }

    try {
      if (editingIndex !== null) {
        const propertyId = globalProperties[editingIndex]._id
        await updateGlobalProperty(propertyId, propertyToSave)
        addNotification("success", "Property updated successfully")
      } else {
        await createGlobalProperty(propertyToSave)
        addNotification("success", "Property added successfully")
      }

      await fetchGlobalProperties()

      const totalPages = getTotalPages(getCombinedProperties());
      setGlobalPropertiesPage(totalPages);

      setShowAddPropertyPopup(false)
      setEditingIndex(null)
      setNewProperty({
        property: "",
        uom: "",
        basis: "volume",
        linear: true,
        min: "",
        max: "",
        description: "",
        index_formula: "",
        reverse_formula: "",
      })
      setFormErrors({})

      window.dispatchEvent(new CustomEvent('globalPropertiesUpdated'));
    } catch (error) {
      addNotification("error", "Failed to save property")
    }
  }

  const handleDeleteGlobalProperty = async (index) => {
    if (window.confirm("Are you sure you want to delete this property?")) {
      try {
        const propertyId = globalProperties[index]._id
        await deleteGlobalProperty(propertyId)

        await fetchGlobalProperties()

        if (globalPropertiesPage > 1 && globalProperties.length <= (globalPropertiesPage - 1) * itemsPerPage) {
          setGlobalPropertiesPage(globalPropertiesPage - 1)
        }

        addNotification("success", "Property deleted successfully")

        window.dispatchEvent(new CustomEvent('globalPropertiesUpdated'));
      } catch (error) {
        addNotification("error", "Failed to delete property")
      }
    }
  }

  const handleClearAllGlobalProperties = async () => {
    if (window.confirm("Are you sure you want to delete all global properties? This action cannot be undone.")) {
      try {
        for (const property of globalProperties) {
          await deleteGlobalProperty(property._id)
        }
        await fetchGlobalProperties()
        setGlobalPropertiesPage(1)
        addNotification("info", "All global properties cleared")

        window.dispatchEvent(new CustomEvent('globalPropertiesUpdated'));
      } catch (error) {
        addNotification("error", "Failed to clear global properties")
      }
    }
  }

  const handleEditGlobalProperty = (index) => {
    const property = globalProperties[index]
    setEditingIndex(index)
    setNewProperty({
      property: property.property,
      uom: property.uom,
      basis: property.basis,
      linear: property.linear,
      min: property.min || "",
      max: property.max || "",
      description: property.description || "",
      index_formula: property.index_formula || "",
      reverse_formula: property.reverse_formula || "",
    })
    setFormErrors({})
    setShowAddPropertyPopup(true)
  }

  const handleDeleteGroup = async (groupId) => {
    if (window.confirm("Are you sure you want to delete this group?")) {
      try {
        await deletePropertyGroup(groupId)
        await fetchPropertyGroups()

        const existingGroups = JSON.parse(localStorage.getItem('propertyGroups') || '[]');
        const updatedGroups = existingGroups.filter(group => group._id !== groupId && group.id !== groupId);
        localStorage.setItem('propertyGroups', JSON.stringify(updatedGroups));

        window.dispatchEvent(new CustomEvent('propertyGroupsUpdated', {
          detail: { groups: updatedGroups }
        }));

        if (savedGroupsPage > 1 && savedGroups.length <= (savedGroupsPage - 1) * itemsPerPage) {
          setSavedGroupsPage(savedGroupsPage - 1)
        }

        addNotification("success", "Group deleted successfully")
      } catch (error) {
        addNotification("error", "Failed to delete property group")
      }
    }
  }

  const handleClearAllGroups = async () => {
    if (window.confirm("Are you sure you want to delete all groups? This action cannot be undone.")) {
      try {
        for (const group of savedGroups) {
          await deletePropertyGroup(group._id || group.id)
        }
        await fetchPropertyGroups()

        localStorage.setItem('propertyGroups', JSON.stringify([]));

        window.dispatchEvent(new CustomEvent('propertyGroupsUpdated', {
          detail: { groups: [] }
        }));

        setSavedGroupsPage(1)
        addNotification("info", "All groups cleared")
      } catch (error) {
        addNotification("error", "Failed to clear groups")
      }
    }
  }

  const toggleGroup = (groupId) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }))
  }

  const handlePropertySelect = (property, source, sourceId, index = null) => {
    const propertyKey = `${source}-${sourceId}-${index !== null ? index : property.property}`

    setSelectedProperties((prev) => {
      const isSelected = prev.some((p) => p.key === propertyKey)
      if (isSelected) {
        return prev.filter((p) => p.key !== propertyKey)
      } else {
        return [
          ...prev,
          {
            ...property,
            key: propertyKey,
            source,
            sourceId,
            originalIndex: index,
            sourceName:
              source === "saved"
                ? savedProperties.find((p) => p.id === sourceId)?.customName ||
                savedProperties.find((p) => p.id === sourceId)?.name
                : source === "group"
                  ? savedGroups.find((g) => g._id === sourceId || g.id === sourceId)?.name
                  : source === "propertySet"
                    ? savedPropertySets.find((s) => s._id === sourceId || s.id === sourceId)?.name
                    : source === "global"
                      ? "Global Properties"
                      : source === "app"
                        ? "App Saved Properties"
                        : "Unknown Source",
          },
        ]
      }
    })
  }

  const isPropertySelected = (property, source, sourceId, index = null) => {
    const key = `${source}-${sourceId}-${index !== null ? index : property.property}`
    return selectedProperties.some((p) => p.key === key)
  }

  const handleSelectAllGlobal = () => {
    const displayProperties = activeSubTab === "global" ? globalProperties : getCombinedProperties();
    if (displayProperties.length === 0) return

    const allSelected = displayProperties.every((prop, index) => isPropertySelected(prop, "global", "global", index))

    if (allSelected) {
      setSelectedProperties((prev) => prev.filter((p) => p.source !== "global"))
    } else {
      displayProperties.forEach((prop, index) => {
        handlePropertySelect(prop, "global", "global", index)
      })
    }
  }

  const handleSelectAllFromGroup = (group) => {
    if (!group.properties || !Array.isArray(group.properties)) return

    const allSelected = group.properties.every((prop, index) => isPropertySelected(prop, "group", group._id || group.id, index))

    if (allSelected) {
      setSelectedProperties((prev) => prev.filter((p) => !(p.source === "group" && p.sourceId === (group._id || group.id))))
    } else {
      group.properties.forEach((prop, index) => {
        handlePropertySelect(prop, "group", group._id || group.id, index)
      })
    }
  }

  const handleSelectAllFromPropertySet = (propertySet) => {
    if (!propertySet.properties || !Array.isArray(propertySet.properties)) return

    const allSelected = propertySet.properties.every((prop, index) =>
      isPropertySelected(prop, "propertySet", propertySet._id || propertySet.id, index),
    )

    if (allSelected) {
      setSelectedProperties((prev) => prev.filter((p) => !(p.source === "propertySet" && p.sourceId === (propertySet._id || propertySet.id))))
    } else {
      propertySet.properties.forEach((prop, index) => {
        handlePropertySelect(prop, "propertySet", propertySet._id || propertySet.id, index)
      })
    }
  }

  const isAllSelectedFromGroup = (group) => {
    if (!group.properties || !Array.isArray(group.properties)) return false
    return group.properties.every((prop, index) => isPropertySelected(prop, "group", group._id || group.id, index))
  }

  const isAllSelectedFromPropertySet = (propertySet) => {
    if (!propertySet.properties || !Array.isArray(propertySet.properties)) return false
    return propertySet.properties.every((prop, index) => isPropertySelected(prop, "propertySet", propertySet._id || propertySet.id, index))
  }

  const isAllGlobalSelected = () => {
    const displayProperties = activeSubTab === "global" ? globalProperties : getCombinedProperties();
    return displayProperties.every((prop, index) => isPropertySelected(prop, "global", "global", index))
  }

  const handleClosePopup = () => {
    setShowAddPropertyPopup(false)
    setEditingIndex(null)
    setNewProperty({
      property: "",
      uom: "",
      basis: "volume",
      linear: true,
      min: "",
      max: "",
      description: "",
      index_formula: "",
      reverse_formula: "",
    })
    setFormErrors({})
  }

  const handleCloseEditPopup = () => {
    setShowEditGroupPopup(false)
    setShowEditPropertySetPopup(false)
    setEditingGroup(null)
    setEditingPropertySet(null)
    setGroupProperties([])
    setEditingPropertySetProperties([])
    setEditingPropertySetMin("")
    setEditingPropertySetMax("")
  }

  const handleAddPropertyFromGroups = () => {
    setShowGradePropertyPopup(true)
    setSelectedProperties([])
    setGroupName("")
    setPropertySetName("")
    setPropertySetMin("")
    setPropertySetMax("")
  }

  const handleCloseGradePropertyPopup = () => {
    setShowGradePropertyPopup(false)
    setSelectedProperties([])
    setGroupName("")
    setPropertySetName("")
    setPropertySetMin("")
    setPropertySetMax("")
  }

  const handlePropertyChange = (field, value) => {
    setNewProperty((prev) => ({
      ...prev,
      [field]: value,
    }))

    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ""
      }))
    }

    if (field === "linear" && value === true) {
      setFormErrors(prev => ({
        ...prev,
        index_formula: "",
        reverse_formula: ""
      }))
    }
  }

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const downloadGlobalCSV = () => {
    const displayProperties = activeSubTab === "global" ? globalProperties : getCombinedProperties();
    const headers = ["Property", "UOM", "Basis", "Blending Type", "Min", "Max", "Description", "Index Formula", "Reverse Formula", "Source"]
    const rows = displayProperties.map((prop) => [
      `"${prop.property || ""}"`,
      `"${prop.uom || ""}"`,
      `"${prop.basis || ""}"`,
      prop.linear ? "Linear" : "Non-Linear",
      `"${prop.min || ""}"`,
      `"${prop.max || ""}"`,
      `"${prop.description || ""}"`,
      `"${prop.index_formula || ""}"`,
      `"${prop.reverse_formula || ""}"`,
      prop.isFromApp ? "App" : prop.isFromReport ? "Report" : "Global"
    ])

    const csvContent = [headers.join(","), ...rows.map((row) => row.join(","))].join("\n")

    const blob = new Blob([csvContent], { type: "text/csv" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `all_properties_${Date.now()}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    addNotification("success", "All properties CSV exported successfully")
  }

  const displayProperties = activeSubTab === "global" ? globalProperties : getCombinedProperties();

  const paginatedDisplayProperties = getPaginatedData(displayProperties, globalPropertiesPage);
  const totalDisplayPages = getTotalPages(displayProperties);

  const paginatedSavedGroups = getPaginatedData(savedGroups, savedGroupsPage)
  const paginatedSavedPropertySets = getPaginatedData(savedPropertySets, savedPropertySetsPage)

  const totalGroupsPages = getTotalPages(savedGroups)
  const totalPropertySetsPages = getTotalPages(savedPropertySets)

  if (!currentUser) {
    return <Login onLogin={() => { }} users={[]} setUsers={() => { }} />
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex">
      <NotificationToaster notifications={notifications} />

      {/* Common Logout Confirmation Modal */}
      <LogoutConfirmModal
        isOpen={showLogoutConfirm}
        onConfirm={handleConfirmLogout}
        onCancel={handleCancelLogout}
      />

      {/* Add Property Popup */}
      {showAddPropertyPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg border border-gray-100 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6 gap-3 sm:gap-0">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3">
                <div className="p-2 bg-purple-50 rounded-lg">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h6a1 1 0 110 2H4a1 1 0 01-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                {editingIndex !== null ? "Edit Property" : "Add New Property"}
              </h2>
            </div>

            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Property Name *</label>
                  <input
                    type="text"
                    value={newProperty.property}
                    onChange={(e) => handlePropertyChange("property", e.target.value)}
                    placeholder="e.g., RON, Density, Viscosity"
                    className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${formErrors.property ? "border-red-300" : "border-gray-300"
                      }`}
                  />
                  {formErrors.property && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.property}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">UOM Name *</label>
                  <input
                    type="text"
                    value={newProperty.uom}
                    onChange={(e) => handlePropertyChange("uom", e.target.value)}
                    placeholder="e.g., %, ppm, mg/L"
                    className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${formErrors.uom ? "border-red-300" : "border-gray-300"
                      }`}
                  />
                  {formErrors.uom && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.uom}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Basis *</label>
                  <div className="relative w-full">
                    <select
                      value={newProperty.basis}
                      onChange={(e) => handlePropertyChange("basis", e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 pr-10 py-2 
                                 focus:ring-2 focus:ring-blue-500 focus:border-transparent 
                                 transition-colors bg-white text-sm appearance-none"
                    >
                      {basisOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <svg
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Blending Type *</label>
                  <div className="relative w-full">
                    <select
                      value={newProperty.linear}
                      onChange={(e) => handlePropertyChange("linear", e.target.value === "true")}
                      className="w-full border border-gray-300 rounded-lg px-3 pr-10 py-2 
                                 focus:ring-2 focus:ring-blue-500 focus:border-transparent 
                                 transition-colors bg-white text-sm appearance-none"
                    >
                      {blendingTypeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <svg
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              {!newProperty.linear && (
                <div className="space-y-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <svg className="w-4 h-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 0v6m0-6h6m-6 0H6"
                      />
                    </svg>
                    <span className="text-sm font-medium text-yellow-800">Non-Linear Property Formulas Required</span>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Index Formula (x → index) *</label>
                    <input
                      type="text"
                      value={newProperty.index_formula}
                      onChange={(e) => handlePropertyChange("index_formula", e.target.value)}
                      placeholder="e.g., math.log10(x), x**2, math.sqrt(x)"
                      className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${formErrors.index_formula ? "border-red-300" : "border-gray-300"
                        }`}
                    />
                    {formErrors.index_formula ? (
                      <p className="mt-1 text-sm text-red-600">{formErrors.index_formula}</p>
                    ) : (
                      <p className="mt-1 text-xs text-gray-500">
                        Formula to convert property value to blending index. Use 'x' as variable.
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reverse Formula (index → value) *
                    </label>
                    <input
                      type="text"
                      value={newProperty.reverse_formula}
                      onChange={(e) => handlePropertyChange("reverse_formula", e.target.value)}
                      placeholder="e.g., 10**x, math.sqrt(x), x**(1/2)"
                      className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${formErrors.reverse_formula ? "border-red-300" : "border-gray-300"
                        }`}
                    />
                    {formErrors.reverse_formula ? (
                      <p className="mt-1 text-sm text-red-600">{formErrors.reverse_formula}</p>
                    ) : (
                      <p className="mt-1 text-xs text-gray-500">
                        Formula to convert blending index back to property value. Use 'x' as variable.
                      </p>
                    )}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={newProperty.description}
                  onChange={(e) => handlePropertyChange("description", e.target.value)}
                  placeholder="Enter property description..."
                  rows="3"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <button
                  onClick={handleClosePopup}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveProperty}
                  disabled={Object.keys(formErrors).length > 0}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium property-btn flex items-center justify-center gap-2 ${Object.keys(formErrors).length > 0
                    ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                    }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {editingIndex !== null ? "Update Property" : "Save Property"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Group Popup */}
      {showEditGroupPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg border border-gray-100 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6 gap-3 sm:gap-0">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3">
                <div className="p-2 bg-orange-50 rounded-lg">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h6a1 1 0 110 2H4a1 1 0 01-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                Edit Group: {editingGroup?.name}
              </h2>
            </div>

            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Available Properties</h3>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {displayProperties.map((property, index) => (
                      <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 truncate">{property.property}</div>
                          <div className="text-sm text-gray-500 truncate">{property.uom} • {property.basis}</div>
                        </div>
                        <button
                          onClick={() => handleAddPropertyToGroup(property)}
                          className="px-3 py-1 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 transition-colors whitespace-nowrap ml-2"
                        >
                          Add
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Group Properties ({groupProperties.length})
                  </h3>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {groupProperties.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">No properties in group</p>
                    ) : (
                      groupProperties.map((property, index) => (
                        <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 truncate">{property.property}</div>
                            <div className="text-sm text-gray-500 truncate">{property.uom} • {property.basis}</div>
                          </div>
                          <button
                            onClick={() => handleRemovePropertyFromGroup(index)}
                            className="px-3 py-1 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 transition-colors whitespace-nowrap ml-2"
                          >
                            Remove
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <button
                  onClick={handleCloseEditPopup}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveGroupEdit}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg font-medium bg-orange flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Property Set Popup */}
      {showEditPropertySetPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg border border-gray-100 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6 gap-3 sm:gap-0">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3">
                <div className="p-2 bg-indigo-50 rounded-lg">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h6a1 1 0 110 2H4a1 1 0 01-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                Edit Property Set: {editingPropertySet?.name}
              </h2>
            </div>

            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Available Properties</h3>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {displayProperties.map((property, index) => (
                      <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 truncate">{property.property}</div>
                          <div className="text-sm text-gray-500 truncate">{property.uom} • {property.basis}</div>
                        </div>
                        <button
                          onClick={() => handleAddPropertyToPropertySet(property)}
                          className="px-3 py-1 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 transition-colors whitespace-nowrap ml-2"
                        >
                          Add
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Set Properties ({editingPropertySetProperties.length})
                  </h3>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {editingPropertySetProperties.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">No properties in set</p>
                    ) : (
                      editingPropertySetProperties.map((property, index) => (
                        <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 truncate">{property.property}</div>
                            <div className="text-sm text-gray-500 truncate">{property.uom} • {property.basis}</div>
                            <div className="flex gap-2 mt-2">
                              <div className="flex-1">
                                <label className="block text-xs text-gray-500 mb-1">Min</label>
                                <input
                                  type="number"
                                  value={property.min || ""}
                                  onChange={(e) => handlePropertyMinMaxChange(index, "min", e.target.value)}
                                  placeholder="Min"
                                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                                />
                              </div>
                              <div className="flex-1">
                                <label className="block text-xs text-gray-500 mb-1">Max</label>
                                <input
                                  type="number"
                                  value={property.max || ""}
                                  onChange={(e) => handlePropertyMinMaxChange(index, "max", e.target.value)}
                                  placeholder="Max"
                                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                                />
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemovePropertyFromPropertySet(index)}
                            className="px-3 py-1 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 transition-colors whitespace-nowrap ml-2"
                          >
                            Remove
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <button
                  onClick={handleCloseEditPopup}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePropertySetEdit}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Grade Property Popup */}
      {showGradePropertyPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl border border-gray-100 max-w-3xl w-full max-h-[95vh] overflow-hidden my-4">
            <div className="px-4 sm:px-6 py-4 bg-gradient-to-r from-orange-50 to-orange-100 border-b border-orange-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg sm:text-xl font-bold text-gray-900">Create Property Group/Set</h2>
                    <p className="text-gray-600 text-xs sm:text-sm">
                      Select properties from all available sources to create a group or property set
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleCloseGradePropertyPopup}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(95vh-120px)]">
              {selectedProperties.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex-1">
                      <h3 className="text-base sm:text-lg font-semibold text-blue-900 mb-2">
                        Selected Properties ({selectedProperties.length})
                      </h3>
                      <div className="flex flex-wrap gap-2 max-h-20 overflow-y-auto">
                        {selectedProperties.map((prop, index) => (
                          <span
                            key={prop.key}
                            className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1"
                          >
                            <span className="truncate max-w-[120px]">{prop.property}</span>
                            <button
                              onClick={() => handlePropertySelect(prop, prop.source, prop.sourceId, prop.originalIndex)}
                              className="text-blue-600 hover:text-blue-800 ml-1 flex-shrink-0"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs sm:text-sm text-blue-700">
                        {activeSubTab === "groups" ? "Group Name" : "Property Set Name"}:{" "}
                        <span className="font-semibold truncate block">
                          {activeSubTab === "groups" ? groupName || "Not set" : propertySetName || "Not set"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-white rounded-xl p-4 sm:p-6 shadow-lg border border-gray-200 mb-4 sm:mb-6">
                <div className="flex flex-col gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {activeSubTab === "groups" ? "Group Name *" : "Property Set Name *"}
                    </label>
                    <input
                      type="text"
                      value={activeSubTab === "groups" ? groupName : propertySetName}
                      onChange={(e) => activeSubTab === "groups" ? setGroupName(e.target.value) : setPropertySetName(e.target.value)}
                      placeholder={activeSubTab === "groups" ? "Enter a name for this property group..." : "Enter a name for this property set..."}
                      className="w-full border border-gray-300 rounded-lg px-3 sm:px-4 py-2 sm:py-3 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
                    />
                  </div>

               

                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={activeSubTab === "groups" ? handleSaveAsGroup : handleSaveAsPropertySet}
                      disabled={(activeSubTab === "groups" ? !groupName.trim() : !propertySetName.trim()) || selectedProperties.length === 0}
                      className={`px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium bg-blue-600 flex items-center justify-center gap-2 ${(activeSubTab === "groups" ? groupName.trim() : propertySetName.trim()) && selectedProperties.length > 0
                        ? "bg-green-600 hover:bg-blue-700 text-white"
                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                        }`}
                    >
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {activeSubTab === "groups" ? "Save Group" : "Save Property Set"} ({selectedProperties.length})
                    </button>
                  </div>
                </div>


                {selectedProperties.length === 0 && (
                  <p className="text-orange-600 text-xs sm:text-sm mt-2 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 0v6m0-6h6m-6 0H6"
                      />
                    </svg>
                    Please select at least one property to create a {activeSubTab === "groups" ? "group" : "property set"}
                  </p>
                )}
              </div>

              <div className="space-y-4 sm:space-y-6">
                {displayProperties.length > 0 && (
                  <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                    <div className="px-4 sm:px-6 py-4 bg-gradient-to-r from-purple-50 to-purple-100 border-b border-purple-200">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div className="w-6 h-6 sm:w-8 sm:h-8 bg-white rounded-lg flex items-center justify-center shadow-sm">
                            <svg
                              className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                              />
                            </svg>
                          </div>
                          <div>
                            <h3 className="text-base sm:text-lg font-bold text-gray-900">
                              {activeSubTab === "global" ? "Global Properties" : "All Properties"}
                            </h3>
                            <p className="text-gray-600 text-xs sm:text-sm">
                              {activeSubTab === "global"
                                ? "Global Properties Only"
                                : "Global Properties + App Properties + Report Properties"}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-2 sm:mt-0">
                          <button
                            onClick={handleSelectAllGlobal}
                            className="px-3 sm:px-4 py-1.5 sm:py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors flex items-center gap-2 text-xs sm:text-sm"
                          >
                            <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 6h16M4 12h16m-7 6h7"
                              />
                            </svg>
                            {isAllGlobalSelected() ? "Deselect All" : "Select All"}
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4 p-4 sm:p-6">
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="bg-gray-50 px-3 sm:px-4 py-3 border-b border-gray-200">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                            <div className="flex items-center gap-2 sm:gap-3">
                              <input
                                type="checkbox"
                                checked={isAllGlobalSelected()}
                                onChange={handleSelectAllGlobal}
                                className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                              />
                              <h4 className="font-semibold text-gray-900 text-sm sm:text-base">
                                {activeSubTab === "global" ? "Global Properties" : "All Properties"}
                              </h4>
                              <span className="bg-white text-gray-700 px-2 py-1 rounded text-xs font-medium">
                                {displayProperties.length} properties
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-gray-500 text-xs sm:text-sm">
                                {isAllGlobalSelected()
                                  ? "All selected"
                                  : `${displayProperties.filter((prop, index) => isPropertySelected(prop, "global", "global", index)).length} selected`}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="bg-white">
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 p-3 sm:p-4">
                            {displayProperties.map((property, index) => (
                              <div
                                key={property._id || property.id || index}
                                className={`border rounded-lg p-3 cursor-pointer transition-all ${isPropertySelected(property, "global", "global", index)
                                  ? "border-green-500 bg-green-50"
                                  : "border-gray-200 hover:border-gray-300"
                                  }`}
                                onClick={() => handlePropertySelect(property, "global", "global", index)}
                              >
                                <div className="flex items-start gap-2 sm:gap-3">
                                  <input
                                    type="checkbox"
                                    checked={isPropertySelected(property, "global", "global", index)}
                                    onChange={() => handlePropertySelect(property, "global", "global", index)}
                                    className="w-4 h-4 text-green-600 rounded focus:ring-green-500 mt-1 flex-shrink-0"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between mb-2">
                                      <span className="font-medium text-gray-900 text-sm truncate">{property.property}</span>
                                      <div className="flex items-center gap-1 ml-2">
                                        <span
                                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${property.linear ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                                            }`}
                                        >
                                          {property.linear ? "Linear" : "Non-Linear"}
                                        </span>
                                        {activeSubTab !== "global" && property.isFromApp && (
                                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                            App
                                          </span>
                                        )}
                                        {activeSubTab !== "global" && property.isFromReport && (
                                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                                            Report
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                                      <div className="truncate">
                                        <span className="text-gray-500">UOM:</span>
                                        <span className="ml-1 truncate">{property.uom || "—"}</span>
                                      </div>
                                      <div className="truncate">
                                        <span className="text-gray-500">Basis:</span>
                                        <span className="ml-1 capitalize truncate">{property.basis || "—"}</span>
                                      </div>
                                    </div>
                                    {property.description && (
                                      <div className="mt-2">
                                        <div className="text-xs text-gray-500 line-clamp-2">
                                          Desc: <span className="text-gray-700">{property.description}</span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {savedGroups.length > 0 && (
                  <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                    <div className="px-4 sm:px-6 py-4 bg-gradient-to-r from-orange-50 to-orange-100 border-b border-orange-200">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div className="w-6 h-6 sm:w-8 sm:h-8 bg-white rounded-lg flex items-center justify-center shadow-sm">
                            <svg
                              className="w-3 h-3 sm:w-4 sm:h-4 text-orange-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                              />
                            </svg>
                          </div>
                          <div>
                            <h3 className="text-base sm:text-lg font-bold text-gray-900">Property Groups</h3>
                            <p className="text-gray-600 text-xs sm:text-sm">Your saved property groups</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4 p-4 sm:p-6">
                      {savedGroups.map((group) => (
                        <div key={group._id} className="border border-gray-200 rounded-lg overflow-hidden">
                          <div className="bg-gray-50 px-3 sm:px-4 py-3 border-b border-gray-200">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                              <div className="flex items-center gap-2 sm:gap-3">
                                <input
                                  type="checkbox"
                                  checked={isAllSelectedFromGroup(group)}
                                  onChange={() => handleSelectAllFromGroup(group)}
                                  className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                                />
                                <div className="flex items-center gap-2">
                                  <h4 className="font-semibold text-gray-900 text-sm sm:text-base">{group.name}</h4>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const newName = prompt("Enter new group name:", group.name);
                                      if (newName && newName.trim() !== "" && newName !== group.name) {
                                        handleUpdateGroupName(group._id, newName.trim());
                                      }
                                    }}
                                    className="text-blue-600 hover:text-blue-800 text-xs"
                                  >
                                    ✏️
                                  </button>
                                </div>
                                <span className="bg-white text-gray-700 px-2 py-1 rounded text-xs font-medium">
                                  {group.properties.length} properties
                                </span>
                              </div>
                              <div className="flex items-center gap-2 mt-2 sm:mt-0">
                                <span className="text-gray-500 text-xs sm:text-sm">
                                  {isAllSelectedFromGroup(group)
                                    ? "All selected"
                                    : `${group.properties.filter((prop, index) => isPropertySelected(prop, "group", group._id, index)).length} selected`}
                                </span>
                                <button
                                  onClick={() => handleEditGroup(group)}
                                  className="px-2 sm:px-3 py-1 bg-blue-600 text-white rounded-lg text-xs sm:text-sm hover:bg-blue-700 transition-colors"
                                >
                                  Edit
                                </button>
                              </div>
                            </div>
                          </div>

                          <div className="bg-white">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 p-3 sm:p-4">
                              {group.properties.map((property, index) => (
                                <div
                                  key={index}
                                  className={`border rounded-lg p-3 cursor-pointer transition-all ${isPropertySelected(property, "group", group._id, index)
                                    ? "border-green-500 bg-green-50"
                                    : "border-gray-200 hover:border-gray-300"
                                    }`}
                                  onClick={() => handlePropertySelect(property, "group", group._id, index)}
                                >
                                  <div className="flex items-start gap-2 sm:gap-3">
                                    <input
                                      type="checkbox"
                                      checked={isPropertySelected(property, "group", group._id, index)}
                                      onChange={() => handlePropertySelect(property, "group", group._id, index)}
                                      className="w-4 h-4 text-green-600 rounded focus:ring-green-500 mt-1 flex-shrink-0"
                                    />
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center justify-between mb-2">
                                        <span className="font-medium text-gray-900 text-sm truncate">{property.property}</span>
                                        <span
                                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${property.linear
                                            ? "bg-green-100 text-green-800"
                                            : "bg-red-100 text-red-800"
                                            }`}
                                        >
                                          {property.linear ? "Linear" : "Non-Linear"}
                                        </span>
                                      </div>
                                      <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                                        <div className="truncate">
                                          <span className="text-gray-500">UOM:</span>
                                          <span className="ml-1 truncate">{property.uom || "—"}</span>
                                        </div>
                                        <div className="truncate">
                                          <span className="text-gray-500">Basis:</span>
                                          <span className="ml-1 capitalize truncate">{property.basis || "—"}</span>
                                        </div>
                                      </div>
                                      {property.description && (
                                        <div className="mt-2">
                                          <div className="text-xs text-gray-500 line-clamp-2">
                                            Desc: <span className="text-gray-700">{property.description}</span>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {savedPropertySets.length > 0 && (
                  <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                    <div className="px-4 sm:px-6 py-4 bg-gradient-to-r from-indigo-100 to-indigo-100 border-b border-indigo-200">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div className="w-6 h-6 sm:w-8 sm:h-8 bg-white rounded-lg flex items-center justify-center shadow-sm">
                            <svg
                              className="w-3 h-3 sm:w-4 sm:h-4 text-indigo-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                              />
                            </svg>
                          </div>
                          <div>
                            <h3 className="text-base sm:text-lg font-bold text-gray-900">Property Sets</h3>
                            <p className="text-gray-600 text-xs sm:text-sm">Your custom property sets</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4 p-4 sm:p-6">
                      {savedPropertySets.map((propertySet) => (
                        <div key={propertySet._id} className="border border-gray-200 rounded-lg overflow-hidden">
                          <div className="bg-gray-50 px-3 sm:px-4 py-3 border-b border-gray-200">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                              <div className="flex items-center gap-2 sm:gap-3">
                                <input
                                  type="checkbox"
                                  checked={isAllSelectedFromPropertySet(propertySet)}
                                  onChange={() => handleSelectAllFromPropertySet(propertySet)}
                                  className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                                />
                                <div className="flex items-center gap-2">
                                  <h4 className="font-semibold text-gray-900 text-sm sm:text-base">{propertySet.name}</h4>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const newName = prompt("Enter new property set name:", propertySet.name);
                                      if (newName && newName.trim() !== "" && newName !== propertySet.name) {
                                        handleUpdatePropertySetName(propertySet._id, newName.trim());
                                      }
                                    }}
                                    className="text-blue-600 hover:text-blue-800 text-xs"
                                  >
                                    ✏️
                                  </button>
                                </div>
                                <span className="bg-white text-gray-700 px-2 py-1 rounded text-xs font-medium">
                                  {propertySet.properties.length} properties
                                </span>
                                {(propertySet.setMin || propertySet.setMax) && (
                                  <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                                    Set Min: {propertySet.setMin || "—"}, Max: {propertySet.setMax || "—"}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 mt-2 sm:mt-0">
                                <span className="text-gray-500 text-xs sm:text-sm">
                                  {isAllSelectedFromPropertySet(propertySet)
                                    ? "All selected"
                                    : `${propertySet.properties.filter((prop, index) => isPropertySelected(prop, "propertySet", propertySet._id, index)).length} selected`}
                                </span>
                                <button
                                  onClick={() => handleEditPropertySet(propertySet)}
                                  className="px-2 sm:px-3 py-1 bg-blue-600 text-white rounded-lg text-xs sm:text-sm hover:bg-blue-700 transition-colors"
                                >
                                  Edit
                                </button>
                              </div>
                            </div>
                          </div>

                          <div className="bg-white">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 p-3 sm:p-4">
                              {propertySet.properties.map((property, index) => (
                                <div
                                  key={index}
                                  className={`border rounded-lg p-3 cursor-pointer transition-all ${isPropertySelected(property, "propertySet", propertySet._id, index)
                                    ? "border-green-500 bg-green-50"
                                    : "border-gray-200 hover:border-gray-300"
                                    }`}
                                  onClick={() => handlePropertySelect(property, "propertySet", propertySet._id, index)}
                                >
                                  <div className="flex items-start gap-2 sm:gap-3">
                                    <input
                                      type="checkbox"
                                      checked={isPropertySelected(property, "propertySet", propertySet._id, index)}
                                      onChange={() => handlePropertySelect(property, "propertySet", propertySet._id, index)}
                                      className="w-4 h-4 text-green-600 rounded focus:ring-green-500 mt-1 flex-shrink-0"
                                    />
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center justify-between mb-2">
                                        <span className="font-medium text-gray-900 text-sm truncate">{property.property}</span>
                                        <span
                                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${property.linear
                                            ? "bg-green-100 text-green-800"
                                            : "bg-red-100 text-red-800"
                                            }`}
                                        >
                                          {property.linear ? "Linear" : "Non-Linear"}
                                        </span>
                                      </div>
                                      <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                                        <div className="truncate">
                                          <span className="text-gray-500">UOM:</span>
                                          <span className="ml-1 truncate">{property.uom || "—"}</span>
                                        </div>
                                        <div className="truncate">
                                          <span className="text-gray-500">Basis:</span>
                                          <span className="ml-1 capitalize truncate">{property.basis || "—"}</span>
                                        </div>
                                      </div>
                                      {(property.min || property.max || property.description) && (
                                        <div className="mt-2 space-y-1">
                                          {property.min && (
                                            <div className="text-xs text-gray-500">
                                              Min: <span className="text-gray-700">{property.min}</span>
                                            </div>
                                          )}
                                          {property.max && (
                                            <div className="text-xs text-gray-500">
                                              Max: <span className="text-gray-700">{property.max}</span>
                                            </div>
                                          )}
                                          {property.description && (
                                            <div className="text-xs text-gray-500 line-clamp-2">
                                              Desc: <span className="text-gray-700">{property.description}</span>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {displayProperties.length === 0 && savedGroups.length === 0 && savedPropertySets.length === 0 && (
                  <div className="bg-white rounded-xl p-6 sm:p-8 text-center border border-gray-200">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">No properties available</h3>
                    <p className="text-gray-500 mb-6 text-sm sm:text-base">Create some properties first to use this feature</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <Navigation
        currentUser={currentUser}
        activeTab={activeTab}
        onTabChange={onTabChange}
        onLogout={handleLogoutClick}
        onShowUserManagement={onShowUserManagement}
        sidebarOpen={sidebarOpen}
        onToggleSidebar={onToggleSidebar}
      />

      <div className={`flex-1 flex flex-col transition-all duration-300 ${sidebarOpen ? "lg:ml-64" : "lg:ml-0"}`}>
        <Header currentUser={currentUser} onTestAPI={() => { }} onToggleSidebar={onToggleSidebar} />

        <main className="flex-1 p-3 sm:p-4 md:p-6 overflow-auto">
          <div className="max-w-7xl mx-auto w-full">

            <div className="mb-4 sm:mb-6">
              <div className="bg-white rounded-xl p-1 shadow-lg border border-gray-200 inline-flex flex-wrap gap-1">
                <button
                  onClick={() => setActiveSubTab("global")}
                  className={`px-3 sm:px-4 md:px-6 py-2 sm:py-3 rounded-lg font-medium transition-all duration-200 text-xs sm:text-sm ${activeSubTab === "global"
                    ? "bg-purple-600 text-white shadow-md"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                    }`}
                >
                  Global Properties
                </button>
                <button
                  onClick={() => setActiveSubTab("groups")}
                  className={`px-3 sm:px-4 md:px-6 py-2 sm:py-3 rounded-lg font-medium transition-all duration-200 text-xs sm:text-sm ${activeSubTab === "groups"
                    ? "bg-orange-600 text-white shadow-md"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                    }`}
                >
                  {/* Groups {savedGroups.length > 0 && `(${savedGroups.length})`} */}
                  Groups
                </button>
                <button
                  onClick={() => setActiveSubTab("propertySets")}
                  className={`px-3 sm:px-4 md:px-6 py-2 sm:py-3 rounded-lg font-medium transition-all duration-200 text-xs sm:text-sm ${activeSubTab === "propertySets"
                    ? "bg-indigo-600 text-white shadow-md"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                    }`}
                >
                  {/* Country Specs {savedPropertySets.length > 0 && `(${savedPropertySets.length})`} */}
                  Country Specs
                </button>
              </div>
            </div>

            <div className="mb-4 sm:mb-6 md:mb-8">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 sm:gap-4">
                <div>
                  <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
                    {activeSubTab === "global"
                      ? "Global Properties"
                      : activeSubTab === "groups"
                        ? "Property Groups"
                        : "Property Sets"}
                  </h1>
                  <p className="text-gray-600 mt-1 sm:mt-2 text-xs sm:text-sm md:text-base">
                    {activeSubTab === "global"
                      ? "Global Properties Only"
                      : activeSubTab === "groups"
                        ? "View and manage your property groups"
                        : "View and manage your property sets"}
                  </p>
                </div>

                <div className="flex flex-wrap gap-2 sm:gap-3">
                  {activeSubTab === "global" ? (
                    <>
                      <button
                        onClick={handleAddPropertyClick}
                        className="property-btn"
                      >
                        <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                          />
                        </svg>
                        Property
                      </button>

                      {globalProperties.length > 0 && (
                        <>
                          <button
                            onClick={downloadGlobalCSV}
                            className="save-all-btn"
                          >
                            <svg
                              className="w-3 h-3 sm:w-4 sm:h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                              />
                            </svg>
                            Export CSV
                          </button>

                          <button
                            onClick={handleClearAllGlobalProperties}
                            className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2 text-xs sm:text-sm"
                          >
                            <svg
                              className="w-3 h-3 sm:w-4 sm:h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                            Clear All
                          </button>
                        </>
                      )}
                    </>
                  ) : activeSubTab === "groups" ? (
                    <>
                      <button
                        onClick={handleAddPropertyFromGroups}
                        className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2 text-xs sm:text-sm"
                      >
                        <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                          />
                        </svg>
                        Group
                      </button>
                      {savedGroups.length > 0 && (
                        <button
                          onClick={handleClearAllGroups}
                          className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2 text-xs sm:text-sm"
                        >
                          <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                          Clear All
                        </button>
                      )}
                    </>
                  ) : activeSubTab === "propertySets" ? (
                    <>
                      <button
                        onClick={handleAddPropertyFromGroups}
                        className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 bg-indigo-700 hover:bg-indigo-800 text-white rounded-lg font-medium transition-colors flex items-center gap-2 text-xs sm:text-sm"
                      >
                        <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                          />
                        </svg>
                        Set
                      </button>
                      {savedPropertySets.length > 0 && (
                        <button
                          onClick={handleClearAllPropertySets}
                          className="px-3 sm:px-4 md:px-6 py-2 sm:py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2 text-xs sm:text-sm"
                        >
                          <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                          Clear All
                        </button>
                      )}
                    </>
                  ) : null}
                </div>
              </div>
            </div>

            {activeSubTab === "global" && (
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                <div className="px-3 sm:px-4 md:px-6 py-4 bg-gradient-to-r from-purple-100 to-purple-100 border-b border-purple-200">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                        <svg
                          className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 text-purple-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                          />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900">Global Properties</h3>
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                          <span className="bg-white text-gray-700 px-2 py-1 rounded text-xs font-medium">
                            {displayProperties.length} {displayProperties.length === 1 ? "property" : "properties"}
                          </span>
                          <span className="bg-white text-gray-700 px-2 py-1 rounded text-xs font-medium">
                            Global Properties Only
                          </span>
                          {hasAutoSaved && (
                            <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium">
                              Auto-saved to Database
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {isLoading.global ? (
                  <LoadingSpinner
                    size="large"
                    text="Loading global properties..."
                  />
                ) : displayProperties.length === 0 ? (
                  <div className="p-6 sm:p-8 text-center">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">No properties yet</h3>
                    <p className="text-gray-500 mb-6 text-sm sm:text-base">Start by adding your first property</p>
                    <button
                      onClick={handleAddPropertyClick}
                      className="px-4 sm:px-6 py-2 sm:py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors text-sm sm:text-base"
                    >
                      Add Property
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                              Property
                            </th>
                            <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                              UOM
                            </th>
                            <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                              Basis
                            </th>
                            <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                              Description
                            </th>
                            <th className="px-3 sm:px-4 md:px-6 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                              Blending Type
                            </th>
                            <th className="px-3 sm:px-4 md:px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {paginatedDisplayProperties.map((property, index) => (
                            <tr key={property._id || property.id || index} className="hover:bg-gray-50 transition-colors">
                              <td className="px-3 sm:px-4 md:px-6 py-4 whitespace-nowrap">
                                <span className="font-medium text-gray-900 text-sm">{property.property}</span>
                              </td>
                              <td className="px-3 sm:px-4 md:px-6 py-4 whitespace-nowrap">
                                <span className="text-gray-900 text-sm">{property.uom || "—"}</span>
                              </td>
                              <td className="px-3 sm:px-4 md:px-6 py-4 whitespace-nowrap">
                                <span className="text-gray-900 text-sm capitalize">{property.basis || "—"}</span>
                              </td>
                              <td className="px-3 sm:px-4 md:px-6 py-4">
                                <span className="text-gray-900 text-sm line-clamp-2">{property.description || "—"}</span>
                              </td>
                              <td className="px-3 sm:px-4 md:px-6 py-4 whitespace-nowrap text-center">
                                <span
                                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${property.linear ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                                    }`}
                                >
                                  {property.linear ? "Linear" : "Non-Linear"}
                                </span>
                              </td>
                              <td className="px-3 sm:px-4 md:px-6 py-4 whitespace-nowrap text-right">
                                {property._id && (
                                  <div className="flex justify-end gap-1 sm:gap-2">
                                    <button
                                      onClick={() => {
                                        const globalIndex = globalProperties.findIndex(p => p._id === property._id);
                                        if (globalIndex !== -1) {
                                          handleEditGlobalProperty(globalIndex);
                                        }
                                      }}
                                      className="px-2 sm:px-3 py-1 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center gap-1 text-xs"
                                    >
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                        />
                                      </svg>
                                      Edit
                                    </button>
                                    <button
                                      onClick={() => {
                                        const globalIndex = globalProperties.findIndex(p => p._id === property._id);
                                        if (globalIndex !== -1) {
                                          handleDeleteGlobalProperty(globalIndex);
                                        }
                                      }}
                                      className="px-2 sm:px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors flex items-center gap-1 text-xs"
                                    >
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                        />
                                      </svg>
                                      Delete
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {renderPagination(
                      globalPropertiesPage,
                      totalDisplayPages,
                      setGlobalPropertiesPage,
                      displayProperties.length,
                    )}
                  </>
                )}
              </div>
            )}

            {/* Groups Tab */}
            {activeSubTab === "groups" && (
              <div className="space-y-4 sm:space-y-6">
                {isLoading.groups ? (
                  <div className="bg-white rounded-xl p-6 sm:p-8 text-center border border-gray-200 shadow-lg">
                    <LoadingSpinner
                      size="large"
                      text="Loading property groups..."
                    />
                  </div>
                ) : savedGroups.length === 0 ? (
                  <div className="bg-white rounded-xl p-6 sm:p-8 text-center border border-gray-200 shadow-lg">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-6 h-6 sm:w-8 sm:h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">No property groups yet</h3>
                    <p className="text-gray-500 mb-6 text-sm sm:text-base">Create your first property group by clicking "Create Group"</p>
                    <button
                      onClick={handleAddPropertyFromGroups}
                      className="px-4 sm:px-6 py-2 sm:py-3 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-colors text-sm sm:text-base"
                    >
                      Create First Group
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4 sm:space-y-6">
                    {paginatedSavedGroups.map((group) => (
                      <div
                        key={group._id}
                        className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden"
                      >
                        <div
                          className="px-4 sm:px-6 py-4 bg-gradient-to-r from-orange-100 to-orange-100 border-b border-orange-200 cursor-pointer hover:bg-orange-200 transition-colors"
                          onClick={() => toggleGroup(group._id)}
                        >
                          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4">
                            <div className="flex items-center gap-3">
                              <div
                                className={`transform transition-transform ${expandedGroups[group._id] ? "rotate-90" : ""}`}
                              >
                                <svg
                                  className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </div>
                              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                                <svg
                                  className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                                  />
                                </svg>
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <h3 className="text-base sm:text-lg font-bold text-gray-900">{group.name}</h3>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const newName = prompt("Enter new group name:", group.name);
                                      if (newName && newName.trim() !== "" && newName !== group.name) {
                                        handleUpdateGroupName(group._id, newName.trim());
                                      }
                                    }}
                                    className="text-blue-600 hover:text-blue-800 text-sm"
                                  >
                                    ✏️
                                  </button>
                                </div>
                                <div className="flex flex-wrap items-center gap-2 mt-1">
                                  <span className="bg-white text-gray-700 px-2 py-1 rounded text-xs font-medium">
                                    {group.properties.length}{" "}
                                    {group.properties.length === 1 ? "property" : "properties"}
                                  </span>
                                  <span className="text-gray-500 text-xs">{formatDate(group.createdAt || group.timestamp)}</span>
                                  <span className="text-gray-500 text-xs">
                                    Click to {expandedGroups[group._id] ? "collapse" : "expand"}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2 mt-3 lg:mt-0" onClick={(e) => e.stopPropagation()}>
                              <button
                                onClick={() => handleEditGroup(group)}
                                className="px-3 sm:px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2 text-xs sm:text-sm"
                              >
                                <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                  />
                                </svg>
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteGroup(group._id)}
                                className="px-3 sm:px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2 text-xs sm:text-sm"
                              >
                                <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>

                        {expandedGroups[group._id] && (
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                    Property
                                  </th>
                                  <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                    UOM
                                  </th>
                                  <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                    Basis
                                  </th>
                                  <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                    Description
                                  </th>
                                  <th className="px-3 sm:px-4 md:px-6 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                                    Blending Type
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {group.properties.map((property, index) => (
                                  <tr key={index} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-3 sm:px-4 md:px-6 py-4 whitespace-nowrap">
                                      <span className="font-medium text-gray-900 text-sm">{property.property}</span>
                                    </td>
                                    <td className="px-3 sm:px-4 md:px-6 py-4 whitespace-nowrap">
                                      <span className="text-gray-900 text-sm">{property.uom || "—"}</span>
                                    </td>
                                    <td className="px-3 sm:px-4 md:px-6 py-4 whitespace-nowrap">
                                      <span className="text-gray-900 text-sm capitalize">{property.basis || "—"}</span>
                                    </td>
                                    <td className="px-3 sm:px-4 md:px-6 py-4">
                                      <span className="text-gray-900 text-sm line-clamp-2">{property.description || "—"}</span>
                                    </td>
                                    <td className="px-3 sm:px-4 md:px-6 py-4 whitespace-nowrap text-center">
                                      <span
                                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${property.linear ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                                          }`}
                                      >
                                        {property.linear ? "Linear" : "Non-Linear"}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    ))}

                    {renderPagination(savedGroupsPage, totalGroupsPages, setSavedGroupsPage, savedGroups.length)}
                  </div>
                )}
              </div>
            )}

            {/* Property Sets Tab */}
            {activeSubTab === "propertySets" && (
              <div className="space-y-4 sm:space-y-6">
                {isLoading.propertySets ? (
                  <div className="bg-white rounded-xl p-6 sm:p-8 text-center border border-gray-200 shadow-lg">
                    <LoadingSpinner
                      size="large"
                      text="Loading property sets..."
                    />
                  </div>
                ) : savedPropertySets.length === 0 ? (
                  <div className="bg-white rounded-xl p-6 sm:p-8 text-center border border-gray-200 shadow-lg">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-6 h-6 sm:w-8 sm:h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">No property sets yet</h3>
                    <p className="text-gray-500 mb-6 text-sm sm:text-base">Create your first property set by clicking "Create Set"</p>
                    <button
                      onClick={handleAddPropertyFromGroups}
                      className="px-4 sm:px-6 py-2 sm:py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors text-sm sm:text-base"
                    >
                      Create First Set
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4 sm:space-y-6">
                    {paginatedSavedPropertySets.map((propertySet) => (
                      <div
                        key={propertySet._id}
                        className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden"
                      >
                        <div
                          className="px-4 sm:px-6 py-4 bg-gradient-to-r from-indigo-50 to-indigo-100 border-b border-indigo-200 cursor-pointer hover:bg-indigo-200 transition-colors"
                          onClick={() => togglePropertySet(propertySet._id)}
                        >
                          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4">
                            <div className="flex items-center gap-3">
                              <div
                                className={`transform transition-transform ${expandedPropertySets[propertySet._id] ? "rotate-90" : ""}`}
                              >
                                <svg
                                  className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </div>
                              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                                <svg
                                  className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                                  />
                                </svg>
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <h3 className="text-base sm:text-lg font-bold text-gray-900">{propertySet.name}</h3>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const newName = prompt("Enter new property set name:", propertySet.name);
                                      if (newName && newName.trim() !== "" && newName !== propertySet.name) {
                                        handleUpdatePropertySetName(propertySet._id, newName.trim());
                                      }
                                    }}
                                    className="text-blue-600 hover:text-blue-800 text-sm"
                                  >
                                    ✏️
                                  </button>
                                </div>
                                <div className="flex flex-wrap items-center gap-2 mt-1">
                                  <span className="bg-white text-gray-700 px-2 py-1 rounded text-xs font-medium">
                                    {propertySet.properties.length}{" "}
                                    {propertySet.properties.length === 1 ? "property" : "properties"}
                                  </span>
                                  {(propertySet.setMin || propertySet.setMax) && (
                                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                                      Set Min: {propertySet.setMin || "—"}, Max: {propertySet.setMax || "—"}
                                    </span>
                                  )}
                                  <span className="text-gray-500 text-xs">{formatDate(propertySet.createdAt || propertySet.timestamp)}</span>
                                  <span className="text-gray-500 text-xs">
                                    Click to {expandedPropertySets[propertySet._id] ? "collapse" : "expand"}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2 mt-3 lg:mt-0" onClick={(e) => e.stopPropagation()}>
                              <button
                                onClick={() => handleEditPropertySet(propertySet)}
                                className="px-3 sm:px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2 text-xs sm:text-sm"
                              >
                                <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                  />
                                </svg>
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeletePropertySet(propertySet._id)}
                                className="px-3 sm:px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2 text-xs sm:text-sm"
                              >
                                <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>

                        {expandedPropertySets[propertySet._id] && (
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                    Property
                                  </th>
                                  <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                    UOM
                                  </th>
                                  <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                    Basis
                                  </th>
                                  <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                    Min
                                  </th>
                                  <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                    Max
                                  </th>
                                  <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                    Description
                                  </th>
                                  <th className="px-3 sm:px-4 md:px-6 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                                    Blending Type
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {propertySet.properties.map((property, index) => (
                                  <tr key={index} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-3 sm:px-4 md:px-6 py-4 whitespace-nowrap">
                                      <span className="font-medium text-gray-900 text-sm">{property.property}</span>
                                    </td>
                                    <td className="px-3 sm:px-4 md:px-6 py-4 whitespace-nowrap">
                                      <span className="text-gray-900 text-sm">{property.uom || "—"}</span>
                                    </td>
                                    <td className="px-3 sm:px-4 md:px-6 py-4 whitespace-nowrap">
                                      <span className="text-gray-900 text-sm capitalize">{property.basis || "—"}</span>
                                    </td>
                                    <td className="px-3 sm:px-4 md:px-6 py-4 whitespace-nowrap">
                                      <span className="text-gray-900 text-sm">{property.min || "—"}</span>
                                    </td>
                                    <td className="px-3 sm:px-4 md:px-6 py-4 whitespace-nowrap">
                                      <span className="text-gray-900 text-sm">{property.max || "—"}</span>
                                    </td>
                                    <td className="px-3 sm:px-4 md:px-6 py-4">
                                      <span className="text-gray-900 text-sm line-clamp-2">{property.description || "—"}</span>
                                    </td>
                                    <td className="px-3 sm:px-4 md:px-6 py-4 whitespace-nowrap text-center">
                                      <span
                                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${property.linear ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                                          }`}
                                      >
                                        {property.linear ? "Linear" : "Non-Linear"}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    ))}

                    {renderPagination(savedPropertySetsPage, totalPropertySetsPages, setSavedPropertySetsPage, savedPropertySets.length)}
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  )
}

export default PropertiesPage